import Button from "@pathwright/ui/src/components/button/Button"
import PropTypes from "prop-types"
import React from "react"
import "./ContentMethodEmbed.css"
import ContentMethodEmbedCode from "./ContentMethodEmbedCode"
import ContentMethodEmbedLink from "./ContentMethodEmbedLink"
import ContentMethodEmbedPreview from "./ContentMethodEmbedPreview"

// This component wraps EmbedLink and EmbedCode. Requires ConnectForm to work correctly.
// Pass fieldsData to EmbedLink and EmbedCode using linkProps and codeProps.

// USAGE:

// fieldsData =
//   link:
//     placeholder: "Paste url here..."
//     load_action: "Load video"
//   code:
//     placeholder: "Paste embed code..."`
//     load_action: "Load_video"
//
// <ContentMethodEmbed
//   defaultMode="code"
//   linkProps={fieldsData.link}
//   codeProps={fieldsData.props}
// />

class ContentMethodEmbed extends React.Component {
  constructor(props) {
    super(props)
    this.handleMode = this.handleMode.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.state = {
      mode: props.defaultMode,
      embed_code:
        __guard__(
          props.linkProps != null ? props.linkProps.value : undefined,
          (x) => x.embed_code
        ) ||
        (props.codeProps != null ? props.codeProps.value : undefined) ||
        null
    }
  }

  handleMode() {
    let nextMode
    switch (this.state.mode) {
      case "embed_code":
        nextMode = "embed_link"
        break
      case "embed_link":
        nextMode = "embed_code"
        break
    }
    return this.setState({
      mode: nextMode
    })
  }

  handleChange(embed_code, embed_link) {
    // bubble to linkProps
    __guardMethod__(this.props.linkProps, "onChange", (o) =>
      o.onChange(
        {
          embed_code,
          embed_link
        },
        this.props.linkProps
      )
    )
    // bubble to codeProps
    return __guardMethod__(this.props.codeProps, "onChange", (o1) =>
      o1.onChange(embed_code, this.props.codeProps)
    )
  }

  render() {
    let embed_code
    return (
      <div className="ContentMethod__Embed">
        <div className="ContentMethod__EmbedContent">
          <ContentMethodEmbedPreview code={this.state.embed_code} />
        </div>
        {(() => {
          // Show either link or embed options
          switch (this.state.mode) {
            case "embed_link":
              return (
                <ContentMethodEmbedLink
                  {...Object.assign({}, this.props.linkProps, {
                    onChange: (embed_link) =>
                      this.handleChange(this.state.embed_code, embed_link),
                    onLoad: ({ embed_code, embed_link }) => {
                      this.handleChange(embed_code, embed_link)
                      return this.setState({ embed_code })
                    }
                  })}
                />
              )
            case "embed_code":
              return (
                <ContentMethodEmbedCode
                  {...Object.assign({}, this.props.codeProps, {
                    onChange: (embed_code) =>
                      this.handleChange(embed_code, null),
                    onLoad: (embed_code) => this.setState({ embed_code })
                  })}
                />
              )
          }
        })()}
        {this.props.allowToggle ? (
          <Button
            className="ContentMethod__EmbedToggle"
            styleType="blank"
            onClick={this.handleMode}
            rounded={false}
            stretch={true}
          >
            {(() => {
              switch (this.state.mode) {
                case "embed_link":
                  return this.props.codePrompt
                case "embed_code":
                  return this.props.linkPrompt
              }
            })()}
          </Button>
        ) : undefined}
      </div>
    )
  }
}

ContentMethodEmbed.displayName = "ContentMethodEmbed"

ContentMethodEmbed.propTypes = {
  // Set default mode
  defaultMode: PropTypes.oneOf(["embed_link", "embed_code"]).isRequired,
  // Show both styles
  allowToggle: PropTypes.bool.isRequired,
  // Customize button to switch mode
  linkPrompt: PropTypes.string.isRequired,
  codePrompt: PropTypes.string.isRequired,
  // Pass props to EmbedLink, EmbedCode
  linkProps: PropTypes.shape({
    // value is object containing link & code
    value: PropTypes.shape({
      embed_link: PropTypes.string,
      embed_code: PropTypes.string
    }),
    // linkProps must at least include onChange func
    onChange: PropTypes.func.isRequired
  }),
  codeProps: PropTypes.shape({
    // value is stringified html
    value: PropTypes.string,
    // codeProps must at least include onChange func
    onChange: PropTypes.func.isRequired
  })
}

ContentMethodEmbed.defaultProps = {
  defaultMode: "embed_link",
  allowToggle: true,
  linkPrompt: "Or paste url instead.",
  codePrompt: "Or paste embed code instead."
}

export default ContentMethodEmbed

function __guardMethod__(obj, methodName, transform) {
  if (
    typeof obj !== "undefined" &&
    obj !== null &&
    typeof obj[methodName] === "function"
  ) {
    return transform(obj, methodName)
  } else {
    return undefined
  }
}
function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null
    ? transform(value)
    : undefined
}
